// VERA-790: internal "Tags" list page — every encoded NFC tag across the platform, associated
// either to a package (production) or directly to a company (company-direct test tags).
import React, { useCallback, useMemo, useState } from 'react'
import type { GridSortModel } from '@mui/x-data-grid-pro'

import { Box, Stack, Typography } from '@mui/material'
import { ArchiveIcon, BuildingIcon, RotateCcwIcon, TagIcon } from 'lucide-react'

import { Button, Modal } from '@repo/ui'
import {
  Header,
  Table,
  EmptyState,
  StatusChip,
  CompanyAvatar,
  CompanyCell,
  LinkCell,
  PackageCell,
  ProductCell,
  EncodeTagsModal,
  PermissionGate,
  ActionDropdown,
  SetPackageCompanyModal,
} from 'components'
import { ROUTES } from 'routes'
import { useCanAccess } from 'hooks'
import { useSnackbar } from 'contexts'
import { useInternalTags, useSetTagStatus } from 'hooks/api/useInternalTags'
import type { InternalTag } from 'hooks/api/useInternalTags'
import { TAG_STATUSES } from 'constants/statuses'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { relativeOrDateTime } from 'utils'

const DEFAULT_PAGINATION = { page: 0, pageSize: 25 }
// The order the API returns unsorted — most-recently-ENCODED first. Handed to the Table as
// `defaultSortModel` so the column header shows the real order, WITHOUT the Sort menu
// treating it as a user-applied sort.
const DEFAULT_SORT: GridSortModel = [{ field: 'encoded_at', sort: 'desc' }]
const cellFlexSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

export const InternalTagsPage = () => {
  const [paginationModel, setPaginationModel] = useState(DEFAULT_PAGINATION)
  // VERA-790 — "Add tags": one modal that picks a company (configure step) then runs the encode
  // session (was a full-page navigation, then a two-modal handoff).
  const [openEncodeTagsModal, setOpenEncodeTagsModal] = useState(false)
  // VERA-790 — Retire confirm target (soft, reversible). Reactivate is a direct undo (no confirm).
  const [retireTarget, setRetireTarget] = useState<InternalTag | null>(null)

  const { open: notification } = useSnackbar()
  // Retire/reactivate is gated to Vera Internal Admin (`internal_tag_retire`). Without it the kebab
  // item is shown DISABLED with a tooltip (§27: in-context actions disable+explain, not hide).
  const { isHasAccess: canRetire } = useCanAccess({ action: 'internal_tag_retire' })
  // VRG-253 — same permission, same role gate (PlatformAdmin) as the package-side assign; the
  // endpoint behind it (`POST packages/tags/assign-company`) requires both.
  const { isHasAccess: canAssignDirectly } = useCanAccess({ action: 'internal_assign_directly' })
  // Tag uids awaiting a company. One uid from the row kebab, N from the bulk action — the modal
  // takes a list either way, so there is no single-vs-bulk fork.
  const [assignUids, setAssignUids] = useState<string[] | null>(null)
  const setTagStatus = useSetTagStatus()

  // Sorting is SERVER-side. The grid only ever holds one page, so a client-side sort would
  // reorder those rows while the header claimed the whole table was sorted — correct-looking
  // on 22 rows, quietly wrong on 500.
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT)
  const activeSort = sortModel[0]

  // VRG-411 — search and the status filter are SERVER-side, for the same reason sorting is:
  // the grid only ever holds one page. A client-side quick filter over those rows answers
  // "is this chip on the page I happen to be looking at?", and returns an empty table —
  // indistinguishable from "no such tag" — for every tag that isn't. On a cross-company
  // inventory whose primary use is scanning a chip to find it, that is a WRONG answer, not
  // a slow one. Passing `onSearchChange` is also what flips the shared Table out of its
  // quickFilterValues fallback (Table.tsx `serverSide={Boolean(onSearchChange)}`).
  //
  // The Search input owns the debounce itself (SEARCH_DEBOUNCE_DELAY, 300ms) and only pushes
  // the settled value up, so there is no second debounce here.
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)

  const { data, isLoading, refetch } = useInternalTags({
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    search: search || undefined,
    status: statusFilter,
    sortBy: activeSort?.field,
    sortDir: (activeSort?.sort ?? undefined) as 'asc' | 'desc' | undefined,
  })

  const rows = data?.data ?? []

  // A new query starts at page 0 — otherwise a narrowed result set is read from whatever
  // offset the previous, wider one was on, and an operator who searched from page 3 gets an
  // empty table for a tag that exists.
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    setPaginationModel((p) => ({ ...p, page: 0 }))
  }, [])

  // The filters drawer reports its APPLIED set here so the page can turn it into query params
  // (Table's `onFiltersChange`). The drawer's statusChip field emits `isAnyOf` with an array;
  // the endpoint's `status` is a single value (`eq`), so one selection is all that can be
  // expressed server-side — matching PackagesPage, which takes the same first-value approach
  // against the same shape of endpoint.
  //
  // `listAll` also takes `companyId` and `family`, and NEITHER is wired here, on purpose:
  //   - Company: the column's auto filter is a text match seeded from the loaded page, while
  //     the endpoint wants a company UUID. The shared `CompanyFilter` does emit ids (see
  //     InternalInvoicesPage) but it is MULTI-select against a single-valued `eq`, so wiring
  //     it would silently drop every selection past the first. That needs the endpoint to
  //     accept a list, not a page-side workaround. Its filter stays page-scoped for now.
  //   - Family: there is no family column and no filter control for it, so there is nothing
  //     to wire. Adding one is a new filter surface, not this fix.
  const handleFiltersChange = useCallback(
    (applied: { field: string; operator?: string; value: unknown }[]) => {
      const raw = applied.find((f) => f.field === 'status')?.value
      const value = Array.isArray(raw) ? raw[0] : raw
      const next = typeof value === 'string' && value in TAG_STATUSES ? value : undefined
      // The drawer re-reports the whole applied set on every commit, so bail when this
      // page's slice of it is unchanged — otherwise an unrelated filter would reset
      // pagination and refire the query.
      if (next === statusFilter) return
      setStatusFilter(next)
      setPaginationModel((p) => ({ ...p, page: 0 }))
    },
    [statusFilter],
  )

  const handleReactivate = useCallback(
    (row: InternalTag) => {
      setTagStatus.mutate(
        { id: row.id, action: 'reactivate' },
        { onError: () => notification({ type: 'error', title: 'Could not reactivate the tag' }) },
      )
    },
    [setTagStatus, notification],
  )

  const confirmRetire = useCallback(() => {
    if (!retireTarget) return
    const { id } = retireTarget
    setRetireTarget(null)
    setTagStatus.mutate(
      { id, action: 'retire' },
      { onError: () => notification({ type: 'error', title: 'Could not retire the tag' }) },
    )
  }, [retireTarget, setTagStatus, notification])

  // One item, chosen by the tag's current state: Retire (active) opens a confirm; Reactivate
  // (retired) fires directly. Both disabled+tooltipped without the permission. Icons per §21.
  const getActionItems = useCallback(
    (row: InternalTag) => {
      const noPermTooltip = canRetire
        ? undefined
        : "You need the 'Retire tags' permission. Ask a Vera admin."
      // §27 — Assign stays visible, disabled+tooltip without the permission. This kebab only
      // ever holds two rows (Assign + Retire/Reactivate), so one disabled row is not the
      // menu-bloat case §27's exception is for — a prior version of this comment argued Assign
      // should HIDE here, which was withdrawn: it was also inconsistent with PackagesPage's
      // identical "Assign to Company" capability, which disables+tooltips.
      const assignTooltip = canAssignDirectly
        ? undefined
        : "You need the 'Assign directly' permission. Ask a Vera admin."
      const assign = [
        {
          label: 'Assign to Company',
          onClick: () => setAssignUids([row.uid]),
          disabled: !canAssignDirectly,
          tooltip: assignTooltip,
          icon: <BuildingIcon strokeWidth={1} />,
        },
      ]
      return row.status === 'retired'
        ? [
            ...assign,
            {
              label: 'Reactivate',
              onClick: () => handleReactivate(row),
              disabled: !canRetire,
              tooltip: noPermTooltip,
              icon: <RotateCcwIcon strokeWidth={1} />,
            },
          ]
        : [
            ...assign,
            {
              label: 'Retire',
              onClick: () => setRetireTarget(row),
              disabled: !canRetire,
              tooltip: noPermTooltip,
              icon: <ArchiveIcon strokeWidth={1} />,
            },
          ]
    },
    [canRetire, canAssignDirectly, handleReactivate],
  )

  // Bulk-select action bar. Assigning tags one at a time is the tedious case this exists for —
  // a freshly-encoded batch is exactly the shape of stock that needs an owner. §27 —
  // disabled+tooltip without the permission, matching the row kebab's item above.
  const getBulkActions = useCallback(
    (selectedRows: InternalTag[]) => [
      {
        label: 'Assign to Company',
        onClick: () => setAssignUids(selectedRows.map((r) => r.uid).filter(Boolean)),
        icon: <BuildingIcon strokeWidth={1} />,
        visible: true,
        disabled: !canAssignDirectly,
        tooltip: canAssignDirectly
          ? undefined
          : "You need the 'Assign directly' permission. Ask a Vera admin.",
      },
    ],
    [canAssignDirectly],
  )

  const columns = useMemo(
    () => [
      {
        field: 'uid',
        // "Tag", not "UID" — the column is the tag's identity on a page already called Tags,
        // and "UID" is the chip-level term, not what an operator calls the thing.
        headerName: 'Tag',
        type: 'string',
        // 180, down from 340. A chip UID is 14 hex characters ("048B3762151990") and the
        // seeded ones are shorter still, so 340 was reserving roughly double what the content
        // can ever occupy — dead width on the pinned column, pushing everything else right.
        width: 180,
        disableColumnMenu: true,
        pinned: 'left',
        renderCell: ({ row }: { row: InternalTag }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body3" sx={{ fontFamily: 'monospace' }} noWrap>
              {row.uid}
            </Typography>
          </Box>
        ),
      },
      {
        // VERA-790 — Company + Package are separate columns so a tag's assignment state is explicit:
        // both empty = Vera's unassigned pool; company only = company-direct test tag; both = a tag
        // in a package (its company is mirrored from the package).
        field: 'company',
        headerName: 'Company',
        type: 'string',
        width: 200,
        disableColumnMenu: true,
        // The SHARED CompanyCell, converged off a hand-rolled 40px avatar + name (§36). That
        // hand-roll was why this column and the Packages one looked different sizes: every
        // other internal table — Companies, Orders, Order detail, Subscriptions, Invoices,
        // Invoice detail, Packages — renders CompanyCell at 48px, and Tags was the lone
        // outlier. Converging also gives the cell what the hand-roll lacked: a link to the
        // company, and the referrer state that puts "← Back to Tags" on that page (§42).
        // Gated on the NAME, not the id. `company_id` is now the EFFECTIVE owner the API
        // resolves with COALESCE(tags.company_id, packages.company_id) — before that, the row
        // carried the raw `tags.company_id`, which is null for a package-derived tag, so a tag
        // owned through its package rendered a blank cell while sitting next to its package and
        // its sibling company-direct tag showed a logo. The name is what the cell displays, so
        // it is the honest presence test; the id is only needed for the link.
        renderCell: ({ row }: { row: InternalTag }) =>
          row.company_name ? (
            <CompanyCell
              company={{
                id: row.company_id,
                name: row.company_name,
                logo: row.company_logo,
                subdomain: (row as any).company_subdomain,
              }}
            />
          ) : null,
      },
      {
        // HIDDEN by default (see the Table's visibilityModel) — most tag work does not care
        // which order the box came from, but when it matters there is no other way to find it.
        // A tag's order is its PACKAGE's order; company-direct tags have none.
        field: 'order',
        headerName: 'Order',
        type: 'string',
        width: 140,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: any }) =>
          row?.order_id ? (
            <LinkCell
              align="left"
              route={ROUTES.INTERNAL_ORDER(encodeURIComponent(String(row.order_id)))}
              link={`#${row.order_id}`}
            />
          ) : (
            '-'
          ),
      },
      {
        field: 'package',
        headerName: 'Package',
        type: 'string',
        width: 400,
        disableColumnMenu: true,
        // `nameEmphasis="unique"` — the timestamp run leads, the SKU drops to the subline. A
        // package name is minted as `<SKU>-<yymmdd>-<hhmm>-<hex>`, and on THIS table the SKU
        // prefix restates the Product column two cells over, so leading with it buried the only
        // part that says which box this tag came out of behind a repeat of what is already on
        // screen. The full name stays in the cell's tooltip.
        renderCell: ({ row }: { row: InternalTag }) =>
          row.package_name ? (
            <Box sx={{ ...cellFlexSx, minWidth: 0 }}>
              <PackageCell
                pkg={{ id: row.package_id, name: row.package_name }}
                size={40}
                nameEmphasis="unique"
              />
            </Box>
          ) : null,
      },
      {
        // HIDDEN by default (see the Table's visibilityModel), like Order — a tag's certificate
        // is not what tag work is usually about, but when it is, this is the only way to get
        // from a chip in your hand to the certificate minted against it.
        field: 'certificate',
        headerName: 'Certificate',
        type: 'string',
        width: 300,
        // No server-side ORDER BY for it (the value is a correlated subquery, not one of
        // `listAll`'s allow-listed sort columns), and sorting is server-side on this table —
        // so offering the header control would produce a sort that silently did nothing.
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: InternalTag }) =>
          row.certificate_id ? (
            <LinkCell
              align="left"
              route={ROUTES.CERTIFICATE(row.certificate_id)}
              link={row.certificate_id}
            />
          ) : (
            '-'
          ),
      },
      {
        field: 'product',
        headerName: 'Product',
        type: 'string',
        width: 240,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: InternalTag }) => {
          if (!row.product_title) return null
          return (
            <ProductCell
              product={{
                images: row.product_image ? [row.product_image] : [],
                title: row.product_title,
                sku: row.product_sku,
              }}
              size={40}
            />
          )
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        // 'statusChip', not 'string' — the drawer renders the same coloured pills the cell
        // shows, over the FULL status set rather than a search box seeded from whatever
        // values happen to be on the loaded page. That is what lets the selection be handed
        // to the server (`handleFiltersChange`); a free-text match on the page could not be.
        type: 'statusChip',
        options: statusFilterOptions(TAG_STATUSES),
        width: 140,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: InternalTag }) => (
          <Box sx={cellFlexSx}>
            <StatusChip
              size="small"
              statuses={TAG_STATUSES}
              status={row.status ?? 'active'}
            />
          </Box>
        ),
      },
      {
        field: 'created_at',
        headerName: 'Created',
        type: 'dateTime',
        width: 190,
        disableColumnMenu: true,
        // relativeOrDateTime, not date(): the house treatment for every dateTime column —
        // "43 minutes ago" for the first 24h, the absolute format after. The shared Table
        // applies it automatically to any `type: 'dateTime'` column that brings no
        // valueFormatter; this column only spells it out because it keeps the table's own
        // body3/secondary cell styling.
        renderCell: ({ row }: { row: InternalTag }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body3" color="text.secondary">
              {row.created_at ? relativeOrDateTime(row.created_at) : '—'}
            </Typography>
          </Box>
        ),
      },
      {
        // Separate from Created on purpose. Re-encoding an existing chip upserts on `uid`, so
        // its `created_at` stays at the row's original date and a Created-sorted table cannot
        // answer "what did I just encode?" — a tag re-encoded a minute ago sits wherever it
        // was first added. This is the table's default sort (newest encode first).
        // Null for legacy CSV-imported tags, whose true encode time was never recorded.
        field: 'encoded_at',
        headerName: 'Encoded',
        type: 'dateTime',
        width: 190,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: InternalTag }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body3" color="text.secondary">
              {row.encoded_at ? relativeOrDateTime(row.encoded_at) : '—'}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: false,
        type: 'string',
        width: 56,
        // The only column with nothing to sort BY — it renders a kebab, not a value.
        sortable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        // Pinned right so the kebab is always visible — without this it sits past the last column
        // and scrolls off the right edge on a narrower viewport.
        pinned: 'right',
        renderCell: ({ row }: { row: InternalTag }) => <ActionDropdown items={getActionItems(row)} />,
      },
    ],
    [getActionItems]
  )

  const renderEmptyState = () => (
    <EmptyState
      title="No tags encoded yet"
      text="Tags you encode — in a package, onto a company, or into the unassigned pool — appear here."
      icon={<TagIcon strokeWidth={1} size={32} />}
    />
  )

  return (
    <>
      <Header
        title="Tags"
        total={data?.total ?? 0}
        rightComponents={
          <PermissionGate permission="internal_package_upload">
            <Button
              variant="primary"
              disableElevation
              onClick={() => setOpenEncodeTagsModal(true)}
              startIcon={<TagIcon strokeWidth={1} />}
            >
              Add tags
            </Button>
          </PermissionGate>
        }
      />

      <Table
        // A scan here resolves to the TAG itself — this list's rows ARE tags, so the
        // scanned id is the search value with no server dependency (VRG-334 free half).
        showScan
        scanEntity="tag"
        // The scan target IS this search box (ActionLine routes a scanned tag id into
        // `onSearchChange`). Without the handler below the scan had nowhere to land at all —
        // it was cleared from the routing layer and never reached the field.
        // `|| undefined` deliberately: the grid seeds its `quickFilterValues` from this prop
        // ONCE at mount, and an empty string there is a live (if vacuous) quick filter.
        search={search || undefined}
        onSearchChange={handleSearchChange}
        onFiltersChange={handleFiltersChange}
        fillBottom
        sortingMode="server"
        defaultSortModel={DEFAULT_SORT}
        onSortModelChange={(model) => {
          // Clearing the sort falls back to the default order rather than to "no order" —
          // the API always orders by something, so an empty model would leave the header
          // showing nothing while the rows stayed sorted.
          setSortModel(model.length ? model : DEFAULT_SORT)
          setPaginationModel((p) => ({ ...p, page: 0 }))
        }}
        loading={isLoading}
        columns={columns}
        rows={rows}
        paginationMode="server"
        rowCount={data?.total ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        // Order and Certificate are HIDDEN by default — most tag work doesn't care which order
        // the box came from or which certificate was minted against the chip, but when it
        // matters there is no other way to find either. Toggle in Columns.
        visibilityModel={{ order: false, certificate: false }}
        bulkActions={getBulkActions}
        emptyState={renderEmptyState()}
      />

      {/* The SAME modal the Packages kebab opens — one "Assign to Company" dialog for both
          units of stock, so the warning copy explaining the consequence can't drift. */}
      <SetPackageCompanyModal
        tagUids={assignUids ?? undefined}
        open={!!assignUids?.length}
        onClose={() => setAssignUids(null)}
        refetch={refetch}
      />

      <EncodeTagsModal
        open={openEncodeTagsModal}
        onClose={() => setOpenEncodeTagsModal(false)}
        refetch={refetch}
      />

      {/* Retire is reversible, so a light confirm (not a red destructive) — see §32: retire ≈ archive. */}
      <Modal
        open={!!retireTarget}
        title="Retire this tag?"
        onClose={() => setRetireTarget(null)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setRetireTarget(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmRetire} loading={setTagStatus.isPending}>
              Retire tag
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
          The tag is marked retired and shown as no longer active. Its record is kept and the chip
          still resolves, so you can reactivate it anytime.
        </Typography>
      </Modal>
    </>
  )
}
